import React, { useEffect, useState } from 'react';
import { Table, message, Input, Select, Row, Col, Button, Space, Card } from 'antd';
import { SearchOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../Config/axiosConfig';
import getClassPublishedColumns from '../../Config/getClassPublishedColumns';
import UpdateClassModal from '../UpdateClassModal/UpdateClassModal';

const { Search } = Input;
const { Option } = Select;

function Published() {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [filters, setFilters] = useState({
    instructor: null,
    search: '',
  });
  
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    pages: 1,
  });
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const navigate = useNavigate();

  // Fetch classes with pagination
  const fetchClasses = async (page = 5, pageSize = 10) => {
    setLoading(true);
    try {
      const payload = {
        pageNumber: page,
        pageSize: pageSize,
      };

      console.log('Fetching classes with payload:', payload);
      
      const response = await axiosInstance.post('/Public/get-paged-classes', payload);
      console.log('API Response:', response);
      
      const { data } = response?.data || {};
      
      if (data && Array.isArray(data.classes)) {
        const classesData = data.classes;
        setClasses(classesData);
        
        // Get pagination info from backend
        const totalCount = data.totalCount || data.total || (data.pages * pageSize);
        const totalPages = data.pages || Math.ceil(totalCount / pageSize);
        
        console.log('Pagination info:', {
          totalCount: totalCount,
          pages: totalPages,
          currentPage: page,
          pageSize: pageSize
        });
        
        setPagination({
          current: page,
          pageSize: pageSize,
          total: totalCount,
          pages: totalPages,
        });

        // Extract unique instructors from ALL classes (not just current page)
        // You might need to fetch all instructors separately
        const uniqueInstructors = [];
        const instructorMap = new Map();
        
        classesData.forEach(cls => {
          if (cls.teacher && !instructorMap.has(cls.teacher.id)) {
            instructorMap.set(cls.teacher.id, true);
            uniqueInstructors.push({
              id: cls.teacher.id,
              name: cls.teacher.name || `${cls.teacher.firstName} ${cls.teacher.lastName}` || 'Unknown'
            });
          }
        });
        
        setInstructors(uniqueInstructors);
      } else {
        console.error('Invalid data structure:', data);
        message.error('No class data received.');
        setClasses([]);
        setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
      console.error('Error details:', error.response?.data || error.message);
      message.error('Error fetching classes');
      setClasses([]);
      setPagination(prev => ({ ...prev, total: 0, pages: 1 }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClasses(pagination.current, pagination.pageSize);
  }, []);

  // Apply filters (client-side for now)
  const applyFilters = () => {
    let result = [...classes];

    // Filter by instructor
    if (filters.instructor) {
      result = result.filter(cls => 
        cls.teacher && cls.teacher.id === filters.instructor
      );
    }

    // Filter by search (topic/title)
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(cls => 
        (cls.title && cls.title.toLowerCase().includes(searchLower)) ||
        (cls.description && cls.description.toLowerCase().includes(searchLower)) ||
        (cls.category && cls.category.name && cls.category.name.toLowerCase().includes(searchLower))
      );
    }

    // Note: Client-side filtering only filters current page
    // For full filtering, you need to fetch all data or implement server-side filtering
    setClasses(result);
    setPagination(prev => ({
      ...prev,
      current: 1,
      total: result.length,
      pages: Math.ceil(result.length / prev.pageSize)
    }));
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Handle search
  const handleSearch = (value) => {
    handleFilterChange('search', value);
  };

  // Reset all filters
  const handleResetFilters = () => {
    setFilters({
      instructor: null,
      search: '',
    });
    fetchClasses(1, pagination.pageSize);
  };

  // Table handlers
  const handleTableChange = (newPagination) => {
    const { current, pageSize } = newPagination;
    fetchClasses(current, pageSize);
  };

  const handleRowClick = (record) => {
    navigate(`/preview-class/${record.id}`);
  };

  const handleEditClass = (record) => {
    setSelectedClass(record);
    setIsModalVisible(true);
  };

  const handleUpdateSuccess = () => {
    fetchClasses(pagination.current, pagination.pageSize);
  };

  const columns = getClassPublishedColumns(
    navigate,
    () => fetchClasses(pagination.current, pagination.pageSize),
    pagination.current,
    handleEditClass
  );

  return (
    <div className="student-admin-container">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Published Classes</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: '#666' }}>
                Page {pagination.current} of {pagination.pages}
              </span>
              <Button 
                icon={<ReloadOutlined />} 
                onClick={() => fetchClasses(pagination.current, pagination.pageSize)}
                loading={loading}
                size="small"
              >
                Refresh
              </Button>
            </div>
          </div>
        }
      >
        {/* Filters Section */}
        <div style={{ marginBottom: 20 }}>
          <Row gutter={[16, 16]} align="middle">
            {/* Instructor Filter */}
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Instructor"
                style={{ width: '100%' }}
                value={filters.instructor}
                onChange={(value) => handleFilterChange('instructor', value)}
                allowClear
                disabled={instructors.length === 0}
              >
                {instructors.map(instructor => (
                  <Option key={instructor.id} value={instructor.id}>
                    {instructor.name}
                  </Option>
                ))}
              </Select>
            </Col>

            {/* Search Filter */}
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by title, description, or category..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                prefix={<SearchOutlined />}
                allowClear
                onPressEnter={() => applyFilters()}
              />
            </Col>

            {/* Filter Actions */}
            <Col xs={24} sm={12} md={8}>
              <Space>
                <Button 
                  onClick={applyFilters}
                  icon={<FilterOutlined />}
                  type="primary"
                >
                  Apply Filters
                </Button>
                <Button 
                  onClick={handleResetFilters}
                  disabled={!filters.instructor && !filters.search}
                >
                  Clear Filters
                </Button>
              </Space>
            </Col>
          </Row>

          {/* Filter Summary */}
          {(filters.instructor || filters.search) && (
            <div style={{ marginTop: 16, padding: 8, backgroundColor: '#f6ffed', borderRadius: 4 }}>
              <span style={{ marginRight: 8 }}>Active Filters:</span>
              {filters.instructor && (
                <span style={{ marginRight: 16 }}>
                  Instructor: <strong>{instructors.find(i => i.id === filters.instructor)?.name}</strong>
                </span>
              )}
              {filters.search && (
                <span>
                  Search: <strong>"{filters.search}"</strong>
                </span>
              )}
            </div>
          )}
        </div>

        {/* Results Count */}
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            Showing {classes.length} of {pagination.total} total classes
          </div>
          <div style={{ fontSize: '12px', color: '#888' }}>
            Total Pages: {pagination.pages}
          </div>
        </div>

        {/* Main Table */}
        <Table
          columns={columns}
          dataSource={classes}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} of ${total} classes`,
            showLessItems: false,
            hideOnSinglePage: false,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: () => handleRowClick(record),
          })}
          scroll={{ x: 'max-content' }}
          bordered
        />
      </Card>

      {/* Update Modal */}
      <UpdateClassModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        classInfo={selectedClass}
        onUpdateSuccess={handleUpdateSuccess}
      />
    </div>
  );
}

export default Published;